import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ProductImage } from '../../ui/ProductImage';
import { HeadingText } from '../shared/HeadingText';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { tierIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { btnOnClay, btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import { AddButton } from '../shared/AddButton';

/**
 * "Waar loopt jouw huid tegenaan?": kies een klacht en zie meteen beide kanten van Beauty Bewust.
 * Links wat je thuis gebruikt (het protocol, los of in één keer in de mand), rechts wat er in
 * de salon bij past. Zo legt dit blok het hele concept uit, zonder dat iemand het hoeft te lezen.
 *
 * De klachten zijn één rij knoppen (op de telefoon schuifbaar); een pil schuift mee naar de
 * keuze. Het advies wisselt met een korte overgang en wordt voorgelezen (live-regio).
 */
export function ConcernFinder() {
  const pages = usePages();
  const {
    concerns,
    routineTotals,
    productsForSkinType,
    routineForConcern,
    treatmentForConcern
  } = useCatalog();
  const [activeId, setActiveId] = useState(concerns[0]?.id);
  const {
    addRoutine
  } = useCartActions();
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const panelId = useId();
  const copy = pages.home.concerns;
  const {
    giftTiers,
    policy
  } = useSite();
  // Eén keer bij binnenkomen: Thuis en Salon komen met een klein hoogteverschil binnen, de salon
  // iets later. Daarna (een andere klacht kiezen) alleen de gewone wissel.
  const gridRef = useRef<HTMLDivElement>(null);
  const entered = useInView(gridRef, {
    once: true,
    margin: '0px 0px -15% 0px'
  });
  const arrive = (offset: number, delay: number) => ({
    initial: reduce || entered ? false : {
      opacity: 0,
      y: offset
    },
    animate: reduce || entered ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: offset
    },
    transition: {
      duration: reduce ? 0 : 0.3,
      delay,
      ease
    }
  });

  // Wissel je onderin het advies van klacht (de balk staat vast), dan begint het nieuwe advies
  // bovenaan, direct onder de balk, in plaats van halverwege een ander verhaal.
  const barRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const choose = (id: string) => {
    setActiveId(id);
    const bar = barRef.current;
    const panel = panelRef.current;
    if (!bar || !panel) return;
    const gap = panel.getBoundingClientRect().top - bar.getBoundingClientRect().bottom;
    if (gap < -1) window.scrollBy({
      top: gap - 8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const active = concerns.find(c => c.id === activeId) ?? concerns[0];
  // Zonder klachten óf zonder protocollen (lege backend) is er niets te kiezen.
  const routine = active ? routineForConcern(active) : undefined;
  if (!active || !routine) return null;
  const treatment = treatmentForConcern(active);
  const totals = routineTotals(routine);
  const matching = productsForSkinType(active.skinType).length;
  const gift = tierIn(giftTiers, totals.price);
  const allSoldOut = isAllSoldOut(totals.items);
  return <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="concern" data-id="element-7288" data-mp="2ayicu31spgx0">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7289" data-mp="1diw1wha64h3j">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7290" data-mp="1dicl53w97yr3">
          <h2 id="concern" className={`lg:col-span-7 ${sectionHeading} text-ink`} data-id="element-7291" data-mp="1igedkffklaf8">
            <HeadingText heading={copy.heading} data-id="element-7292" data-mp="3k63ja1lbhyjh" />
          </h2>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-7293" data-mp="3g5jkyup0covr">{fill(copy.body)}</p>}
        </div>

        {/*
          Telefoon: het advies is lang (protocol, producten, salon). De klachten blijven daarom
          bovenin staan zolang je door dit blok scrolt, zodat je kunt wisselen zonder terug te
          scrollen. Computer: alles past in beeld, dus gewoon op zijn plek.
         */}
        <div ref={barRef} className="sticky top-0 z-sticky -mx-6 mt-10 bg-shell py-3 lg:static lg:mx-0 lg:py-0" data-id="element-7294" data-mp="2r6fe6ksdli4">
        <ul aria-label="Kies je huidklacht" className="flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] lg:flex-wrap lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7295" data-mp="gp3g9f69we2l">
          {concerns.map(c => {
            const isActive = c.id === active.id;
            return <li key={c.id} className="shrink-0" data-id="element-7296" data-mp="2djhmr9hjnsun">
                <button type="button" aria-pressed={isActive} aria-controls={panelId} onClick={() => choose(c.id)} className={`relative inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full border px-5 text-[15px] transition-[color,border-color] duration-150 ease-soft ${isActive ? 'border-ink text-shell' : 'border-ink/15 text-ink hover:border-ink/50'}`} data-id="element-7297" data-mp="t0dvoclct798">
                  {isActive && <motion.span layoutId="concern-pill" aria-hidden="true" className="absolute inset-[-1px] rounded-full bg-ink" transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7298" data-mp="f9secsbjt5dg" />}
                  <span className="relative" data-id="element-7299" data-mp="1vnqy1yq6dd1z">{c.label}</span>
                </button>
              </li>;
          })}
        </ul>
        </div>

        {/* Live-regio: na een keuze hoort een schermlezer dat het advies is gewisseld. */}
        <div ref={panelRef} id={panelId} aria-live="polite" className="mt-5 scroll-mt-24 lg:mt-8" data-id="element-7300" data-mp="33mxxntu78fo6">
          <AnimatePresence mode="wait" initial={false} data-id="element-7301" data-mp="2zktandy9ahai">
            <motion.div key={active.id} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease
          }} data-id="element-7302" data-mp="2zlcpumwiurku">
              <p className="max-w-3xl text-[17px] leading-relaxed text-muted" data-id="element-7303" data-mp="3nbqcfxwc7y81">{active.description}</p>

              <div ref={gridRef} className="mt-8 grid gap-4 lg:grid-cols-12 lg:gap-6" data-id="element-7304" data-mp="3iinoq6e8rizr">
                {/* Thuis */}
                <motion.article {...arrive(24, 0)} aria-label={`Thuis bij ${active.label.toLowerCase()}`} className="flex flex-col bg-sand p-6 sm:p-8 lg:col-span-7 lg:p-10" data-id="element-7305" data-mp="3dklv4rj6w1a1">
                  <p className="text-[13px] text-muted" data-id="element-7306" data-mp="a33wnapb46ma">Thuis</p>
                  <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight text-ink" data-id="element-7307" data-mp="hfjq4xb4iv4l">{routine.name}</h3>

                  <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-7308" data-mp="mdl8th4n0sfj">
                    {totals.items.map(p => {
                    const soldOut = isSoldOut(p);
                    return <li key={p.id} className="group flex items-center gap-3 py-3" data-id="element-7309" data-mp="vfuhe1gcibqa">
                          <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 p-1 transition-transform duration-150 ease-soft group-hover:-translate-y-1" data-id="element-7310" data-mp="10d4f04dw1tp7" />
                          <div className="min-w-0 flex-1" data-id="element-7311" data-mp="1f2ip5epagf5n">
                            <p className="text-[12px] text-clay" data-id="element-7312" data-mp="1j55m1u0tgq9p">{soldOut ? shopLabels.soldOutShort : p.step}</p>
                            <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7313" data-mp="1o2ys8n24dk9z">
                              {p.name}
                            </Link>
                          </div>
                          <span className="shrink-0 text-[14px] tabular-nums text-muted" data-id="element-7314" data-mp="2jcutr5jfza0s">{euro(p.price)}</span>
                          <AddButton product={p} data-id="element-7315" data-mp="2ee1h1fbzbn1o" />
                        </li>;
                  })}
                  </ul>

                  <div className="mt-auto pt-7" data-id="element-7316" data-mp="1qop8mhoh7j0f">
                    {!allSoldOut && <button type="button" onClick={() => addRoutine(routine.id)} className={`${btnPrice} sm:w-auto sm:min-w-[20rem]`} data-id="element-7317" data-mp="1lqfca3r0mcd8">
                        <span className={btnPriceLabel} data-id="element-7318" data-mp="1vl7btd45r9an">Protocol in de mand</span>
                        <span className={btnPriceAmount} data-id="element-7319" data-mp="17vv46hohb9ag">{euro(totals.price)}</span>
                      </button>}
                    <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-7320" data-mp="3elyh5jo073bo">
                      Gaat {totals.months} mee · met huidplan op papier
                      {gift ? ` · ${gift.title} ligt voor je klaar` : ''}
                      {matching > 0 && <>
                          {' · '}
                          <Link to={`/webshop?huid=${encodeURIComponent(active.skinType)}`} className="underline underline-offset-4 hover:text-ink" data-id="element-7321" data-mp="39nwy2yqkv407">
                            alle {plural(matching, 'product', 'producten')} voor {active.skinType.toLowerCase()}
                          </Link>
                        </>}
                    </p>
                  </div>
                </motion.article>

                {/* In de salon: klei, de diepte van de site. Alleen shell-tekst (5,6:1). */}
                {/* Niet uitrekken tot de hoogte van "Thuis": anders een groot leeg klei-vlak onderaan. */}
                <motion.article {...arrive(48, 0.08)} aria-label={`In de salon bij ${active.label.toLowerCase()}`} className="flex flex-col bg-clay p-6 text-shell sm:p-8 lg:col-span-5 lg:self-start lg:p-10" data-id="element-7322" data-mp="2gzrbs7jvl6y9">
                  <p className="text-[13px]" data-id="element-7323" data-mp="3f7sg2bl9by3x">In de salon</p>
                  {treatment ? <>
                      <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight" data-id="element-7324" data-mp="3j8vpogcpoxwp">{treatment.name}</h3>
                      {treatment.tagline && <p className="mt-4 text-[16px] leading-relaxed" data-id="element-7325" data-mp="3a9qvfm86nqqc">{treatment.tagline}</p>}
                      <dl className="mt-6 grid grid-cols-2 border-y border-shell/25 text-[14px]" data-id="element-7326" data-mp="2w9zaf1j91a2u">
                        <div className="py-3 pr-4" data-id="element-7327" data-mp="30bkv3920ysnl">
                          <dt className="text-[12px]" data-id="element-7328" data-mp="30ccfdnq693yk">Duur</dt>
                          <dd className="mt-0.5 font-display text-xl" data-id="element-7329" data-mp="ui2znu4wo494">{treatmentDuration(treatment)}</dd>
                        </div>
                        <div className="border-l border-shell/25 py-3 pl-4" data-id="element-7330" data-mp="yiwpit1wrt4n">
                          <dt className="text-[12px]" data-id="element-7331" data-mp="2j1gz2acd1sck">Prijs</dt>
                          <dd className="mt-0.5 font-display text-xl" data-id="element-7332" data-mp="2izw62p5p2hgt">{treatmentPrice(treatment)}</dd>
                        </div>
                      </dl>
                    </> : <>
                      <h3 className="mt-1 font-display text-[1.7rem] font-light leading-tight" data-id="element-7333" data-mp="2o0tmaw7591fo">Liever eerst laten kijken?</h3>
                      <p className="mt-4 text-[16px] leading-relaxed" data-id="element-7334" data-mp="32owp5wpw3x09">
                        In een {policy.consult.name.toLowerCase()} van {policy.consult.minutes} minuten kijk ik met je mee en stel ik je plan op.
                      </p>
                    </>}
                  <div className="pt-7" data-id="element-7335" data-mp="36sw44aviunri">
                    <button type="button" onClick={() => openBooking(treatment?.slug ?? policy.consult.slug)} aria-haspopup="dialog" className={btnOnClay} data-id="element-7336" data-mp="36pqdusb5pfu7">
                      {treatment ? 'Plan deze behandeling' : `Plan een ${policy.consult.name.toLowerCase()}`}
                    </button>
                  </div>
                </motion.article>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}